import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTiptap, useTiptapState } from "@tiptap/react";
import { faH1, faH2, faH3, faH4, faH5, faH6, faItalic, faUnderline, faStrikethrough, faPalette, faLineHeight, faHeading, faAlignLeft, faTextSize } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks/redux";
import { TWebsiteBuilderRichTextResponsiveFontSize } from "../extensions/websiteBuilderRichTextResponsiveFontSize";
import { WebsiteBuilderRichTextColorPicker } from "./WebsiteBuilderRichTextColorPicker";
import { WebsiteBuilderRichTextSelect } from "./WebsiteBuilderRichTextSelect";
import { TWebsiteBuilderRichTextMenuPlacement } from "./WebsiteBuilderRichTextMenu";
import { useSectionEntityPreviewContext } from "../../designSpace/views/components/sectionEntity/SectionEntityPreviewContext";

interface IWebsiteBuilderHeadingRichTextMenuProps {
  elementId: string;
  menuPlacement: TWebsiteBuilderRichTextMenuPlacement;
}

type TWebsiteBuilderHeadingRichTextMenuOptionKey =
  | "heading-level"
  | "italic"
  | "underline"
  | "strike"
  | "color"
  | "line-height"
  | "text-align"
  | "font-size";

interface IWebsiteBuilderHeadingRichTextMenuOption {
  key: TWebsiteBuilderHeadingRichTextMenuOptionKey;
  label: string;
  icon: IconDefinition;
  selected: boolean;
  onClick?: () => void;
}

const HEADING_LEVEL_OPTIONS = [1, 2, 3, 4, 5, 6];
const HEADING_LEVEL_ICONS: Record<number, IconDefinition> = { 1: faH1, 2: faH2, 3: faH3, 4: faH4, 5: faH5, 6: faH6 };
const HEADING_FONT_SIZE_OPTIONS = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "36px", "40px", "48px", "56px", "64px", "72px", "96px"];
const HEADING_LINE_HEIGHT_OPTIONS = ["1", "1.25", "1.5", "1.75", "2"];

export function WebsiteBuilderHeadingRichTextMenu(props: IWebsiteBuilderHeadingRichTextMenuProps) {
  const { elementId, menuPlacement } = props;

  const { t } = useTranslation();

  const { editor } = useTiptap();

  const { setPreview } = useSectionEntityPreviewContext();

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const colorButtonRef = useRef<HTMLButtonElement>(null);

  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  const editorState = useTiptapState(({ editor }) => {
    const responsiveFontSize: TWebsiteBuilderRichTextResponsiveFontSize = editor.state.doc.attrs.responsiveFontSize;
    const level: unknown = editor.getAttributes("heading").level;
    const lineHeight: unknown = editor.getAttributes("heading").lineHeight;
    const textAlign: unknown = editor.getAttributes("heading").textAlign;

    return {
      responsiveFontSize,
      level: typeof level === "number" ? level : 1,
      italic: editor.isActive("italic"),
      underline: editor.isActive("underline"),
      strike: editor.isActive("strike"),
      lineHeight: typeof lineHeight === "string" ? lineHeight : null,
      textAlign: typeof textAlign === "string" ? textAlign : null,
    };
  });

  const fontSize = editorState.responsiveFontSize[deviceView];

  const previewHeadingLevel = useCallback((level: number) => {
    setPreview(() => {
      if (level == null) {
        return null;
      } else {
        return { type: "element", element: { type: "heading", level } };
      }
    });
  }, []);

  useEffect(() => {
    return () => setPreview(null);
  }, []);

  function updateFontSize(value: string) {
    if (value === "") {
      editor.chain().focus().unsetResponsiveFontSize(deviceView).run();
    } else {
      editor.chain().focus().setResponsiveFontSize(deviceView, value).run();
    }
  }

  function updateHeadingLevel(level: number) {
    editor.chain().focus().updateAttributes("heading", { level }).run();
  }

  function updateLineHeight(lineHeight: string) {
    if (lineHeight === "") {
      editor.chain().focus().resetAttributes("heading", "lineHeight").run();
    } else {
      editor.chain().focus().updateAttributes("heading", { lineHeight }).run();
    }
  }

  function updateTextAlign(textAlign: string) {
    if (textAlign === "") {
      editor.chain().focus().unsetTextAlign().run();
    } else {
      editor.chain().focus().setTextAlign(textAlign).run();
    }
  }

  const options: IWebsiteBuilderHeadingRichTextMenuOption[] = [
    {
      key: "italic",
      label: t("Italic"),
      icon: faItalic,
      selected: editorState.italic,
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      key: "underline",
      label: t("Underline"),
      icon: faUnderline,
      selected: editorState.underline,
      onClick: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      key: "strike",
      label: t("Strikethrough"),
      icon: faStrikethrough,
      selected: editorState.strike,
      onClick: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      key: "color",
      label: t("Color"),
      icon: faPalette,
      selected: colorPickerOpen,
      onClick: () => setColorPickerOpen(prev => !prev),
    },
    {
      key: "heading-level",
      label: t("Heading Level"),
      icon: HEADING_LEVEL_ICONS[editorState.level] ?? faHeading,
      selected: false,
    },
    {
      key: "font-size",
      label: t("Font Size"),
      icon: faTextSize,
      selected: fontSize != null,
    },
    {
      key: "line-height",
      label: t("Line Height"),
      icon: faLineHeight,
      selected: editorState.lineHeight != null,
    },
    {
      key: "text-align",
      label: t("Text Align"),
      icon: faAlignLeft,
      selected: editorState.textAlign != null,
    },
  ];

  return (
    <>
      {options.map(option => (
        <OverlayTrigger
          key={option.key}
          placement={menuPlacement}
          delay={{
            show: 500,
            hide: 100,
          }}
          overlay={
            <Tooltip id={`website-builder-rich-text-menu-option-tooltip-${elementId}-${option.key}`}>
              {option.label}
            </Tooltip>
          }
        >
          <div
            className={classNames("website-builder-rich-text-menu-option", { selected: option.selected })}
          >
            {option.key === "heading-level" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-heading-level`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={editorState.level}
                options={HEADING_LEVEL_OPTIONS.map(value => ({ value, label: `H${value}` }))}
                onChange={updateHeadingLevel}
                onPreview={previewHeadingLevel}
              />
            ) : option.key === "font-size" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-font-size`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={fontSize ?? ""}
                options={[
                  { value: "", label: t("Default") },
                  ...(fontSize != null && !HEADING_FONT_SIZE_OPTIONS.includes(fontSize)
                    ? [{ value: fontSize, label: fontSize }] : []),
                  ...HEADING_FONT_SIZE_OPTIONS.map(value => ({ value, label: value })),
                ]}
                onChange={updateFontSize}
              />
            ) : option.key === "line-height" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-line-height`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={editorState.lineHeight ?? ""}
                options={[
                  { value: "", label: t("Default") },
                  ...(editorState.lineHeight != null && !HEADING_LINE_HEIGHT_OPTIONS.includes(editorState.lineHeight)
                    ? [{ value: editorState.lineHeight, label: editorState.lineHeight }] : []),
                  ...HEADING_LINE_HEIGHT_OPTIONS.map(value => ({ value, label: value })),
                ]}
                onChange={updateLineHeight}
              />
            ) : option.key === "text-align" ? (
              <WebsiteBuilderRichTextSelect
                id={`${elementId}-text-align`}
                label={option.label}
                trigger={<FontAwesomeIcon icon={option.icon} />}
                value={editorState.textAlign ?? ""}
                options={[
                  { value: "", label: t("Default") },
                  { value: "left", label: t("Left") },
                  { value: "center", label: t("Center") },
                  { value: "right", label: t("Right") },
                ]}
                onChange={updateTextAlign}
              />
            ) : (
              <button
                ref={option.key === "color" ? colorButtonRef : undefined}
                type="button"
                className="rich-text-menu-button"
                onClick={option.onClick}
              >
                <FontAwesomeIcon icon={option.icon} />
              </button>
            )}
          </div>
        </OverlayTrigger>
      ))}
      {colorPickerOpen && (
        <WebsiteBuilderRichTextColorPicker
          elementId={elementId}
          colorButtonRef={colorButtonRef}
          closeColorPicker={() => setColorPickerOpen(false)}
        />
      )}
    </>
  );
}
